import { AnimatePresence, motion } from 'framer-motion';
import { Dispatch, ReactNode, SetStateAction, useEffect } from 'react';
import useMeasure from 'react-use-measure';
import IconPlus from '/public/icons/plus2.svg';
import IconMinus from '/public/icons/minus.svg';
import {
  cvaHeaderInner,
  cvaItem,
  cvaItemContent,
  cvaItemHeader,
  cvaItemWrapper,
  cvaPlus,
  cvaPlusIcon,
} from './InfoAccordionStyles';
import { twMergeExt } from '@/shared/utils/twMergeExt';

interface Props {
  i: number;
  header: string;
  expanded: number | boolean;
  setExpanded: Dispatch<SetStateAction<number | false>>;
  children: ReactNode;
  forceShow: boolean;
  onAnimationStart?: () => void;
  onAnimationEnd?: () => void;
}

const Accordion = ({
  i,
  header,
  expanded,
  setExpanded,
  forceShow,
  children,
  onAnimationStart,
  onAnimationEnd,
}: Props) => {
  const isOpen = forceShow || i === expanded;

  const [accordionItemRef, accordionItemRefBounds] = useMeasure();
  const [accordionItemContentRef, accordionItemContentRefBounds] = useMeasure();
  const [itemWrapperRef, itemWrapperRefBounds] = useMeasure();

  useEffect(() => {
    window.dispatchEvent(new Event('resize'));
  }, [itemWrapperRefBounds.height]);

  return (
    <div className={cvaItem()} ref={accordionItemRef} data-cursor={'-pointer'}>
      <div
        onClick={() => setExpanded(isOpen ? false : i)}
        className={cvaItemHeader({
          state: isOpen ? 'active' : !isOpen && typeof expanded === 'number' ? 'notActive' : 'default',
        })}
      >
        <div className={cvaHeaderInner()}>
          <div
            dangerouslySetInnerHTML={{
              __html: header,
            }}
          ></div>
          <div className={cvaPlus()}>
            {isOpen ? <IconMinus className={cvaPlusIcon()} /> : <IconPlus className={cvaPlusIcon()} />}
          </div>
        </div>
      </div>

      <AnimatePresence initial={false}>
        {isOpen ? (
          <motion.div
            ref={itemWrapperRef}
            initial="collapsed"
            animate="open"
            exit="collapsed"
            variants={{
              open: {
                opacity: 1,
                height: accordionItemContentRefBounds.height,
              },
              collapsed: {
                opacity: 0,
                height: 0,
              },
            }}
            transition={{ duration: 0.3 }}
            className={twMergeExt(cvaItemWrapper())}
            onAnimationStart={onAnimationStart}
            onAnimationComplete={(variant) => {
              onAnimationEnd?.();
              if (accordionItemRefBounds.top < 0 && variant === 'open') {
                const scrollOffset = accordionItemRefBounds.top + +scrollY;

                window.scroll({
                  top: scrollOffset,
                  behavior: 'smooth',
                });
              }
            }}
          >
            <motion.div
              initial="collapsed"
              animate="open"
              exit="collapsed"
              variants={{
                open: { opacity: 1 },
                collapsed: { opacity: 0 },
              }}
              transition={{ duration: 0.3 }}
              className={twMergeExt(cvaItemContent(), 'nojs-opacity1')}
              ref={accordionItemContentRef}
            >
              {children}
            </motion.div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
};

export default Accordion;
